import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Search, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/tracker", label: "Tracker" },
  { to: "/timeline", label: "Timeline" },
  { to: "/categories", label: "Categories" },
  { to: "/jurisdictions", label: "Jurisdictions" },
  { to: "/about", label: "Methodology" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const config = usePublicConfig();
  const editorEmail = config.editorial_contact_email;
  const { user } = useAuth();
  const adminEmail = user?.email;

  const onSubmitTip = () => navigate("/submit");

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F5F5] text-[#111111]">
      {/* Top utility bar */}
      <div className="border-b border-black/15 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 flex justify-between items-center h-8 text-[11px] font-mono-plex tracking-wider uppercase text-[#555]">
          <span data-testid="utility-tagline">A civic accountability project · evidence first · receipts always</span>
          <span className="hidden md:inline">EST. 2026</span>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-black/15">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-8 h-8 bg-black flex items-center justify-center">
              <div className="w-3 h-3 bg-white" />
            </div>
            <div className="leading-none">
              <div className="font-display text-2xl font-black tracking-tight">RECEIPTS</div>
              <div className="font-mono-plex text-[11px] tracking-[0.25em] text-[#555] uppercase">/Women's Rights Tracker</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `font-sans-plex text-sm tracking-wide uppercase ${isActive ? "text-black border-b-2 border-black pb-1" : "text-[#444] hover:text-black"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="outline"
              data-testid="header-search-btn"
              className="rounded-none border-black text-black hover:bg-black hover:text-white h-10"
              onClick={() => navigate("/tracker")}
            >
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
            <Button
              data-testid="submit-tip-cta"
              className="rounded-none bg-black hover:bg-[#333] text-white h-10 font-medium tracking-wide"
              onClick={onSubmitTip}
            >
              Submit a Tip
            </Button>
            {adminEmail ? (
              <Button
                variant="outline"
                data-testid="admin-dash-link"
                className="rounded-none border-[#0050FF] text-[#0050FF] hover:bg-[#0050FF] hover:text-white h-10"
                onClick={() => navigate("/admin")}
              >
                Admin
              </Button>
            ) : null}
          </div>

          <button
            className="lg:hidden p-2 border border-black"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-black/15 bg-white">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="text-sm uppercase tracking-wide" onClick={() => setOpen(false)}>
                  {n.label}
                </Link>
              ))}
              <Button
                onClick={() => { setOpen(false); navigate("/submit"); }}
                className="rounded-none bg-black text-white"
                data-testid="mobile-submit-tip"
              >
                Submit a Tip
              </Button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-black text-white mt-16">
        <div className="max-w-[1400px] mx-auto px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="font-display text-3xl font-black">RECEIPTS</div>
            <p className="font-mono-plex text-xs tracking-[0.2em] uppercase text-white/60 mt-2">
              Women's Rights Accountability Tracker
            </p>
            <p className="text-sm text-white/80 mt-4 max-w-md leading-relaxed">
              An independent, citation-first record of policy actions affecting
              women's rights across U.S. federal and state government. Every claim
              has a receipt.
            </p>
          </div>
          <div>
            <div className="overline !text-white/80">Sections</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/tracker">Tracker</Link></li>
              <li><Link to="/timeline">Timeline</Link></li>
              <li><Link to="/jurisdictions">Jurisdictions</Link></li>
              <li><Link to="/categories">Categories</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline !text-white/80">Participate</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/submit">Submit a Tip</Link></li>
              <li><Link to="/about">Methodology</Link></li>
              {editorEmail && (
                <li>
                  <a
                    href={`mailto:${editorEmail}?subject=${encodeURIComponent("Receipts — editor inquiry")}`}
                    data-testid="footer-contact-editor"
                    className="inline-flex items-center gap-1.5"
                  >
                    <Mail className="w-3.5 h-3.5" /> Contact an editor
                  </a>
                </li>
              )}
              <li><Link to="/admin/login">Admin</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col md:flex-row justify-between text-xs text-white/80 font-mono-plex uppercase tracking-wider gap-2">
            <span>© 2026 Receipts. All citations belong to their publishers.</span>
            <span>Built for the public record.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
